import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { api, clearAuthToken, clearElevatedToken, setAuthToken } from '@/api/client'
import { httpStatus } from '@/lib/http-status'
import type { LoginOutcome } from '@/types/twoFactor'

export interface Agent {
  id: string
  name: string
  email: string
  phone: string
  photo?: string
  agency_name: string
  license_number: string
  verified: number
  specialization?: string
  languages?: string | string[]
  rating?: number
  review_count?: number
  bio?: string
  role?: string
  platform_role?: 'platform_admin' | null
  slug?: string
  ui_mode?: string
  uiMode?: string
  onboarding_status?: string
  onboarding_stage?: string
  onboarding_steps?: Record<string, boolean>
  preferred_locale?: string
  [key: string]: unknown
}

/** Transient /me failure that must NOT wipe the session (D-1). */
export type SessionDegradedReason = 'rate_limited' | 'unavailable'

interface AuthContextType {
  agent: Agent | null
  loading: boolean
  isAdmin: boolean
  /** Set when /me failed without an auth rejection; token is still kept. */
  sessionDegraded: SessionDegradedReason | null
  retrySession: () => Promise<void>
  /**
   * Resolves to `{ status: '2fa_required', … }` when the password was correct
   * but the account has a second factor — no session is established in that
   * case. Callers must handle both outcomes.
   */
  login: (email: string, password: string) => Promise<LoginOutcome>
  /** Redeems a sign-in challenge and establishes the session. */
  completeTwoFactor: (challengeId: string, code: string) => Promise<void>
  register: (data: Record<string, unknown>) => Promise<void>
  logout: () => void
  refreshAgent: () => Promise<void>
  updateProfile: (data: Record<string, unknown>) => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  agent: null,
  loading: true,
  isAdmin: false,
  sessionDegraded: null,
  retrySession: async () => {},
  login: async () => ({ status: 'signed_in' }),
  completeTwoFactor: async () => {},
  register: async () => {},
  logout: () => {},
  refreshAgent: async () => {},
  updateProfile: async () => {},
})

/** True only for real auth rejection — not 429 / 5xx / network. */
export function shouldClearSessionOnMeFailure(err: unknown): boolean {
  const status = httpStatus(err)
  return status === 401 || status === 403
}

function degradedReasonFromError(err: unknown): SessionDegradedReason {
  return httpStatus(err) === 429 ? 'rate_limited' : 'unavailable'
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [agent, setAgent] = useState<Agent | null>(null)
  const [loading, setLoading] = useState(true)
  const [sessionDegraded, setSessionDegraded] = useState<SessionDegradedReason | null>(null)

  const loadSession = useCallback(async () => {
    const token = localStorage.getItem('fi_token') || localStorage.getItem('sa_token')
    if (!token) {
      setAgent(null)
      setSessionDegraded(null)
      return
    }
    try {
      const data = await api.me() as Agent
      setAgent(data)
      setSessionDegraded(null)
    } catch (err: unknown) {
      if (shouldClearSessionOnMeFailure(err)) {
        clearAuthToken()
        setAgent(null)
        setSessionDegraded(null)
        return
      }
      // Keep fi_token — rate limits / outages are not invalid sessions (D-1).
      setSessionDegraded(degradedReasonFromError(err))
    }
  }, [])

  useEffect(() => {
    const legacy = localStorage.getItem('sa_token')
    if (legacy && !localStorage.getItem('fi_token')) {
      setAuthToken(legacy)
    }
    const token = localStorage.getItem('fi_token') || localStorage.getItem('sa_token')
    if (!token) {
      setLoading(false)
      return
    }
    void loadSession().finally(() => setLoading(false))
  }, [loadSession])

  const retrySession = useCallback(async () => {
    setLoading(true)
    try {
      await loadSession()
    } finally {
      setLoading(false)
    }
  }, [loadSession])

  /** Adopt a session payload from either /auth/login or /auth/2fa/challenge. */
  const adoptSession = async (res: { token?: string; agent?: Agent }) => {
    if (!res?.token) throw new Error('Sign-in succeeded but no token was returned')
    setAuthToken(res.token)
    // A fresh session starts unelevated — never carry a previous account's
    // step-up across a sign-in.
    clearElevatedToken()
    // Prefer fresh /me payload so role/affiliation match server state
    try {
      const me = await api.me()
      setAgent(me)
      setSessionDegraded(null)
    } catch (err: unknown) {
      if (shouldClearSessionOnMeFailure(err)) {
        clearAuthToken()
        setAgent(null)
        setSessionDegraded(null)
        throw err
      }
      setAgent(res.agent ?? null)
      setSessionDegraded(degradedReasonFromError(err))
    }
  }

  const login = async (email: string, password: string): Promise<LoginOutcome> => {
    const res = await api.login(email, password)
    // Password accepted, but the account has a second factor. Deliberately not
    // an error: the caller shows a code prompt rather than a failure message.
    if (res?.status === '2fa_required') {
      return { status: '2fa_required', challenge_id: res.challenge_id, method: res.method }
    }
    await adoptSession(res)
    return { status: 'signed_in' }
  }

  const completeTwoFactor = async (challengeId: string, code: string) => {
    const res = await api.twoFactorChallenge(challengeId, code)
    await adoptSession(res)
  }

  const register = async (data: Record<string, unknown>) => {
    const res = await api.register(data)
    if (!res?.token) throw new Error('Registration succeeded but no token was returned')
    setAuthToken(res.token)
    try {
      const me = await api.me()
      setAgent(me)
      setSessionDegraded(null)
    } catch (err: unknown) {
      if (shouldClearSessionOnMeFailure(err)) {
        clearAuthToken()
        setAgent(null)
        setSessionDegraded(null)
        throw err
      }
      setAgent(res.agent)
      setSessionDegraded(degradedReasonFromError(err))
    }
  }

  const logout = () => {
    clearAuthToken()
    clearElevatedToken()
    setAgent(null)
    setSessionDegraded(null)
    window.location.href = '/'
  }

  const refreshAgent = async () => {
    const data = await api.me()
    setAgent(data)
    setSessionDegraded(null)
  }

  const updateProfile = async (data: Record<string, unknown>) => {
    const updated = await api.updateProfile(data)
    setAgent(updated)
  }

  const isAdmin = !!agent && agent.platform_role === 'platform_admin'

  return (
    <AuthContext.Provider
      value={{
        agent,
        loading,
        isAdmin,
        sessionDegraded,
        retrySession,
        login,
        completeTwoFactor,
        register,
        logout,
        refreshAgent,
        updateProfile,
      }}
    >
      {sessionDegraded ? (
        <div
          role="alert"
          className="border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-950"
        >
          {sessionDegraded === 'rate_limited'
            ? 'We could not refresh your session because the service is rate-limiting requests. You are still signed in.'
            : 'We could not refresh your session. You are still signed in — try again shortly.'}
          {' '}
          <button
            type="button"
            className="font-medium underline underline-offset-2"
            onClick={() => { void retrySession() }}
          >
            Retry
          </button>
        </div>
      ) : null}
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
